import { useMemo, useEffect, useRef } from 'react'
import { format } from 'date-fns'
import { Layers, ShoppingBag } from 'lucide-react'
import { Skeleton } from '@/components/ui/Skeleton'
import { POSTerminal } from './POSTerminal'
import { useTables, useTableCombinations } from '@/lib/queries/useTables'
import { usePOSOrders, useUpdateOrderStatus, useEnsurePOSOrderForBooking } from '@/lib/queries/usePOS'
import { useUIStore } from '@/lib/store/useUIStore'
import { formatPOSMoney, usePOSCurrency } from '@/lib/pos/currency'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '@/lib/db/database'
import type { POSOrder } from '@/lib/types/pos'
import type { TableCombination } from '@/lib/types/table'

const TODAY = format(new Date(), 'yyyy-MM-dd')
const COUNTER_SALE_ID = '__counter_sale__'

// ── Status helpers ────────────────────────────────────────────────────────────

type TableStatusKey = 'free' | 'open' | 'in_progress' | 'paid'

function getTableStatus(order: POSOrder | undefined): TableStatusKey {
  if (!order || order.status === 'voided' || order.status === 'closed') return 'free'
  if (order.status === 'open') return 'open'
  if (order.status === 'sent' || order.status === 'partially_paid') return 'in_progress'
  if (order.status === 'paid') return 'paid'
  return 'free'
}

const STATUS_STYLE: Record<TableStatusKey, { border: string; bg: string; label: string; labelColor: string; dot: string }> = {
  free:        { border: 'border-[var(--color-border-subtle)]', bg: 'bg-[var(--color-bg-surface)]',  label: 'Free',           labelColor: 'text-[var(--color-text-muted)]', dot: 'bg-[var(--color-bg-elevated)]' },
  open:        { border: 'border-green-500/40',                 bg: 'bg-green-500/5',                label: 'Open',           labelColor: 'text-green-400',                dot: 'bg-green-400' },
  in_progress: { border: 'border-amber-500/40',                 bg: 'bg-amber-500/5',                label: 'In progress',    labelColor: 'text-amber-400',                dot: 'bg-amber-400' },
  paid:        { border: 'border-blue-500/30',                  bg: 'bg-blue-500/5',                 label: 'Paid · Free',    labelColor: 'text-blue-400',                 dot: 'bg-blue-400' },
}

function orderTotal(order: POSOrder) {
  return (order.orderItems ?? [])
    .filter((i: any) => i.status !== 'voided')
    .reduce((sum: number, i: any) => sum + i.unitPrice * i.quantity, 0)
}

// ── Table card ────────────────────────────────────────────────────────────────

function TableCard({
  name,
  areaName,
  seats,
  order,
  isCombined,
  combinedTableNames,
  bookingInfo,
  money,
  onClick,
  onFreeTable,
}: {
  name:               string
  areaName?:          string
  seats:              number
  order:              POSOrder | undefined
  isCombined:         boolean
  combinedTableNames?: string[]
  bookingInfo?:       { guestName: string; people: number }
  money:              (amount: number) => string
  onClick:            () => void
  onFreeTable?:       (orderDocumentId: string) => void
}) {
  const statusKey = getTableStatus(order)
  const s         = STATUS_STYLE[statusKey]
  const total     = order ? orderTotal(order) : 0
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onClick()
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={[
        'relative flex cursor-pointer flex-col p-4 rounded-[var(--radius-md)] border-2 text-left transition-all hover:shadow-md active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-amber)]/50',
        s.bg,
        s.border,
      ].join(' ')}
    >
      {/* Status dot */}
      <span className={`absolute top-3 right-3 h-2.5 w-2.5 rounded-full ${s.dot}`} />

      {/* Combined badge */}
      {isCombined && (
        <span className="absolute top-3 left-3 flex items-center gap-0.5 text-[10px] font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-full">
          <Layers className="h-2.5 w-2.5" />
          combined
        </span>
      )}

      {/* Table name */}
      <p className={`text-lg font-bold text-[var(--color-text-primary)] leading-none truncate pr-4 ${isCombined ? 'mt-5' : ''}`}>
        {name}
      </p>

      {/* Area or combined sub-tables */}
      {isCombined && combinedTableNames && (
        <p className="text-xs text-[var(--color-text-muted)] mt-1 truncate">
          {combinedTableNames.join(' + ')}
        </p>
      )}
      {!isCombined && areaName && (
        <p className="text-xs text-[var(--color-text-muted)] mt-1 truncate">{areaName}</p>
      )}

      {/* Booking guest name */}
      {bookingInfo?.guestName && (
        <p className="text-xs font-medium text-amber-400 mt-1 truncate">{bookingInfo.guestName}</p>
      )}

      {/* Seats / covers */}
      <p className="text-xs text-[var(--color-text-muted)] mt-3">
        {bookingInfo ? `${bookingInfo.people} guests` : `${seats} seat${seats !== 1 ? 's' : ''}`}
      </p>

      {/* Status + total */}
      <div className="flex items-center justify-between mt-1.5">
        <span className={`text-xs font-medium ${s.labelColor}`}>{s.label}</span>
        {order && total > 0 && (
          <span className="text-xs font-semibold text-[var(--color-text-primary)]">
            {money(total)}
          </span>
        )}
      </div>

      {/* Free Table button — only on paid cards */}
      {statusKey === 'paid' && onFreeTable && order && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onFreeTable(order.documentId) }}
          className="mt-2 w-full text-[11px] font-medium py-1 rounded-[var(--radius-sm)] bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 transition-colors"
        >
          Free Table
        </button>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function POSTablesView({ allowCounterSale = true }: { allowCounterSale?: boolean }) {
  const selectedDocId    = useUIStore((s) => s.posSelectedTableDocumentId)
  const setSelectedDocId = useUIStore((s) => s.setPosSelectedTableDocumentId)
  const currency = usePOSCurrency()
  const money = useMemo(() => (amount: number) => formatPOSMoney(amount, currency), [currency])

  const freeTable = useUpdateOrderStatus()
  const handleFreeTable = (orderDocumentId: string) =>
    freeTable.mutate({ orderDocumentId, status: 'closed' })

  const { data: tablesResp,  isLoading: tablesLoading }      = useTables()
  const { data: combosResp,  isLoading: combosLoading }      = useTableCombinations()
  // No date filter — fetch all active orders regardless of when they were opened
  // (orders spanning midnight would otherwise disappear from the floor view)
  const { data: ordersResp }                                  = usePOSOrders(undefined, { refetchInterval: 30_000 })

  const tables: any[]              = (tablesResp as any)?.data ?? []
  const combinations: TableCombination[] = (combosResp as any)?.data ?? []
  const orders: POSOrder[]         = (ordersResp as any)?.data ?? []

  // ── Auto-create POS orders for arrived bookings ───────────────────────────
  const arrivedBookings = useLiveQuery(
    () => db.bookings.filter((b) => b.status === 'arrived' && b.date === TODAY).toArray(),
    [],
  )

  const ensurePOSOrder = useEnsurePOSOrderForBooking()
  const triggeredRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!arrivedBookings) return
    const activeOrderBookingIds = new Set(
      orders
        .filter((o) => !['voided', 'closed', 'paid'].includes(o.status) && o.booking?.documentId)
        .map((o) => o.booking!.documentId),
    )
    for (const booking of arrivedBookings) {
      if (triggeredRef.current.has(booking.documentId)) continue
      triggeredRef.current.add(booking.documentId)
      if (activeOrderBookingIds.has(booking.documentId)) continue
      ensurePOSOrder.mutate({
        bookingDocumentId: booking.documentId,
        tableDocumentId: booking.bookingTables?.[0]?.table?.documentId,
        covers: booking.people,
        silent: true,
      })
    }
  }, [arrivedBookings, orders, ensurePOSOrder])

  // Map table documentId → arrived booking info (for card display)
  const bookingByTableDocId = useMemo(() => {
    const map = new Map<string, { guestName: string; people: number }>()
    for (const booking of arrivedBookings ?? []) {
      for (const bt of booking.bookingTables ?? []) {
        if (bt.table?.documentId) {
          map.set(bt.table.documentId, {
            guestName: booking.guest?.name ?? '',
            people: booking.people,
          })
        }
      }
    }
    return map
  }, [arrivedBookings])

  // Build table documentId → best active order
  const orderByTable = useMemo(() => {
    const map = new Map<string, POSOrder>()
    for (const o of orders) {
      if (!o.table?.documentId || o.status === 'voided' || o.status === 'closed') continue
      const existing = map.get(o.table.documentId)
      if (!existing || o.openedAt > existing.openedAt) map.set(o.table.documentId, o)
    }
    return map
  }, [orders])

  // Set of table IDs that belong to a registered combination
  const combinedTableIds = useMemo(() => {
    const ids = new Set<number>()
    for (const combo of combinations) {
      for (const t of combo.tables ?? []) ids.add(t.id)
    }
    return ids
  }, [combinations])

  // Build "booking groups": standalone tables grouped by a shared active order
  // (e.g. Left 3 + Right 1 assigned to the same 8-person booking).
  // Groups with >1 table render as a single combined card.
  const { bookingGroups, bookingGroupedDocIds } = useMemo(() => {
    // order documentId → all booking table docIds (only for standalone tables)
    const byOrder = new Map<string, { order: POSOrder; tableDocIds: string[]; tableNames: string[]; totalSeats: number }>()
    for (const o of orders) {
      if (!o.table?.documentId || o.status === 'voided' || o.status === 'closed') continue
      if (!o.booking?.bookingTables || o.booking.bookingTables.length < 2) continue
      const allDocs = o.booking.bookingTables
        .map((bt) => bt.table?.documentId)
        .filter(Boolean) as string[]
      if (allDocs.length < 2) continue
      // Only group if all tables are standalone (not in a registered combo)
      const allStandalone = allDocs.every((docId) => {
        const t = tables.find((t: any) => t.documentId === docId)
        return t && !combinedTableIds.has(t.id)
      })
      if (!allStandalone) continue
      byOrder.set(o.documentId, {
        order: o,
        tableDocIds: allDocs,
        tableNames: o.booking.bookingTables.map((bt) => bt.table?.name ?? '').filter(Boolean),
        totalSeats: allDocs.reduce((sum, docId) => {
          const t = tables.find((t: any) => t.documentId === docId)
          return sum + (t?.seatsMax ?? 0)
        }, 0),
      })
    }
    const groups = Array.from(byOrder.values())
    const groupedDocIds = new Set(groups.flatMap((g) => g.tableDocIds))
    return { bookingGroups: groups, bookingGroupedDocIds: groupedDocIds }
  }, [orders, tables, combinedTableIds])

  // Standalone tables: not in a registered combo AND not in a booking group
  const standaloneTables = useMemo(
    () => tables.filter((t: any) => !combinedTableIds.has(t.id) && !bookingGroupedDocIds.has(t.documentId)),
    [tables, combinedTableIds, bookingGroupedDocIds],
  )
  const activeTableOrderCount = useMemo(
    () => orders.filter((o) => o.table?.documentId && !['voided', 'closed', 'paid'].includes(o.status)).length,
    [orders],
  )

  // Resolve selected entry: standalone table, registered combo, or booking group
  const selectedTable = tables.find((t: any) => t.documentId === selectedDocId)
  const selectedCombo = combinations.find(
    (c) => (c.tables ?? [])[0]?.documentId === selectedDocId,
  )
  const selectedGroup = bookingGroups.find((g) => g.tableDocIds[0] === selectedDocId)

  if (allowCounterSale && selectedDocId === COUNTER_SALE_ID) {
    return (
      <POSTerminal
        tableName="Counter Sale"
        serviceMode="counter"
        onBack={() => setSelectedDocId(null)}
      />
    )
  }

  if (selectedDocId && (selectedTable || selectedCombo || selectedGroup)) {
    const name = selectedCombo
      ? selectedCombo.name
      : selectedGroup
        ? selectedGroup.tableNames.join(' + ')
        : selectedTable.name
    return (
      <POSTerminal
        tableDocumentId={selectedDocId}
        tableName={name}
        onBack={() => setSelectedDocId(null)}
      />
    )
  }

  const isLoading = tablesLoading || combosLoading

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-2">
        <div className="flex items-center gap-4 text-xs text-[var(--color-text-muted)]">
          <span>
            <span className="font-semibold text-[var(--color-text-primary)]">{standaloneTables.length + combinations.length + bookingGroups.length}</span> floor spots
          </span>
          <span>
            <span className="font-semibold text-[var(--color-text-primary)]">{activeTableOrderCount}</span> active orders
          </span>
        </div>
        {allowCounterSale && (
          <button
            type="button"
            onClick={() => setSelectedDocId(COUNTER_SALE_ID)}
            className="inline-flex items-center gap-2 rounded-[var(--radius-md)] bg-[var(--color-accent-amber)] px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-[var(--color-accent-amber)]/90"
          >
            <ShoppingBag className="h-4 w-4" />
            Counter Sale
          </button>
        )}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={120} />
          ))}
        </div>
      ) : standaloneTables.length === 0 && combinations.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)] py-12 text-center">
          No tables configured.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 overflow-y-auto pb-4 min-h-0">

          {/* Combined table cards */}
          {combinations.map((combo) => {
            const anchorDocId = (combo.tables ?? [])[0]?.documentId
            if (!anchorDocId) return null

            // Best order across all tables in the combo
            const comboOrder = (combo.tables ?? [])
              .map((t) => orderByTable.get(t.documentId))
              .filter(Boolean)
              .sort((a, b) => (b!.openedAt > a!.openedAt ? 1 : -1))[0]

            return (
              <TableCard
                key={combo.documentId}
                name={combo.name}
                seats={combo.seatsMax}
                order={comboOrder}
                isCombined
                combinedTableNames={(combo.tables ?? []).map((t) => t.name)}
                money={money}
                onClick={() => setSelectedDocId(anchorDocId)}
                onFreeTable={handleFreeTable}
              />
            )
          })}

          {/* Booking-grouped cards (multi-table bookings, no registered combo) */}
          {bookingGroups.map((group) => (
            <TableCard
              key={group.order.documentId}
              name={group.tableNames.join(' + ')}
              seats={group.totalSeats}
              order={group.order}
              isCombined
              combinedTableNames={group.tableNames}
              bookingInfo={bookingByTableDocId.get(group.tableDocIds[0])}
              money={money}
              onClick={() => setSelectedDocId(group.tableDocIds[0])}
              onFreeTable={handleFreeTable}
            />
          ))}

          {/* Standalone table cards */}
          {standaloneTables.map((table: any) => (
            <TableCard
              key={table.documentId}
              name={table.name}
              areaName={table.area?.name}
              seats={table.seatsMax}
              order={orderByTable.get(table.documentId)}
              isCombined={false}
              bookingInfo={bookingByTableDocId.get(table.documentId)}
              money={money}
              onClick={() => setSelectedDocId(table.documentId)}
              onFreeTable={handleFreeTable}
            />
          ))}
        </div>
      )}
    </div>
  )
}
